"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.consentElementsToChoiceQuestions = consentElementsToChoiceQuestions;
const choice_options_1 = require("./choice_options");
const signer_roles_1 = require("./signer_roles");
/** Editor stores geometry as fractions of the page (0–1). Template `form.json` uses CSS percentages (0–100). */
function fractionToCssPercent(fraction) {
    return Math.round(fraction * 10000) / 100;
}
/**
 * Orders options as they are printed on the document: by page, then down the page, then across it.
 *
 * The overlays are absolutely positioned, so keyboard and screen-reader order follows the order the
 * host mounts them in — i.e. this array — not their visual position. Emitting in authoring order would
 * mean an author who placed the third option first hands the signer a question whose options are
 * announced out of sequence.
 */
function sortByReadingOrder(options) {
    return [...options].sort((a, b) => a.page - b.page || a.position.yPct - b.position.yPct || a.position.xPct - b.position.xPct);
}
/**
 * The question's own place in reading order: its first option that is actually on a page. An unplaced
 * option still carries the geometry it was created with, so filtering on `placed` is the only way to
 * tell "at the top of page 1" from "not on the document yet".
 */
function firstPlacedOption(element) {
    var _a;
    const [first] = sortByReadingOrder(((_a = element.config.ec__options) !== null && _a !== void 0 ? _a : []).filter((option) => option.placed));
    return first;
}
/**
 * Maps SINGLE_SELECT / MULTI_SELECT elements to `questions` for `templates.consent.framework.app~1_0`.
 * Emits one entry per question with every option nested under `choices`, so question-level fields
 * (`signerType`/`role`/`optional`/`isMultiSelect`) aren't repeated per option. No signer-visible option
 * text is emitted — only the integer key and a backend-only reference `value` — so the overlay relies on
 * contextual position next to the PDF's printed label and needs no translation.
 *
 * `ec__key` correlates back to `ec__requested_data[].ec__key`; each option's `optionKey` matches the key
 * half of the corresponding `ec__selection_options` pair, letting the host submit `ec__key`→`optionKey`
 * selections to Cortex.
 *
 * Lives here rather than in the editor because the Firebase `consent_bundler` owns compilation while the
 * editor owns authoring — both need the identical wire shape.
 *
 * @param createError builds the thrown error, so `consent_bundler` can surface a 400 `ServiceError`
 *   while other callers get a plain `Error`.
 */
function consentElementsToChoiceQuestions(elements, roles = [], createError = (message) => new Error(message)) {
    return (elements
        .filter((el) => (0, choice_options_1.isChoiceElementType)(el.type))
        // Questions follow the same reading order as the options inside them, keyed off each question's
        // first placed option. A question with nothing placed yet has no position to sort on, so it goes
        // last rather than sorting as if it sat at the top-left of page 1.
        .sort((a, b) => {
        const first = firstPlacedOption(a);
        const second = firstPlacedOption(b);
        if (!first || !second) {
            return Number(!first) - Number(!second);
        }
        return (first.page - second.page ||
            first.position.yPct - second.position.yPct ||
            first.position.xPct - second.position.xPct);
    })
        .map((el) => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (typeof el.config.ec__key !== 'string' || el.config.ec__key.length === 0) {
            throw createError(`Consent bundler: choice element is missing ec__key (type=${el.type}, role=${(_a = el.config.ec__signer_role) !== null && _a !== void 0 ? _a : ''}). Every choice question must have a stable ec__key so the host can correlate selections to ec__requested_data.`);
        }
        return {
            ec__key: el.config.ec__key,
            isMultiSelect: el.type === 'MULTI_SELECT',
            signerType: (_c = (_b = (0, signer_roles_1.findRoleByLabel)(el.config.ec__signer_role, roles)) === null || _b === void 0 ? void 0 : _b.signerTypeId) !== null && _c !== void 0 ? _c : 'participant',
            role: (0, signer_roles_1.getSignerRoleLabel)(el.config.ec__signer_role),
            optional: (_d = el.config.ec__optional) !== null && _d !== void 0 ? _d : false,
            order: (_e = el.config.ec__order) !== null && _e !== void 0 ? _e : '0',
            // The printed question text, pasted in by the author. Carried into the bundle so the runtime
            // can name each option for a screen reader; never rendered visually.
            questionLabel: (_f = el.config.ec__question_label) !== null && _f !== void 0 ? _f : '',
            choices: sortByReadingOrder((_g = el.config.ec__options) !== null && _g !== void 0 ? _g : []).map((option) => {
                var _a;
                return ({
                    optionKey: option.key,
                    // Backend-only reference value (never rendered). Forwarded to the host so it
                    // can submit `ec__value` in the `key=value` format Cortex's set_data expects.
                    // Falls back to the option key so a blank reference label never collapses to an
                    // empty value that Cortex/read-side can't map back to a selection.
                    value: (0, choice_options_1.choiceOptionReferenceValue)(option),
                    // The printed response, in the document's language — what a screen reader announces for
                    // this option. Kept separate from `value`, which is a locale-stable data key.
                    localizedAriaLabel: (_a = option.localizedAriaLabel) !== null && _a !== void 0 ? _a : '',
                    page: option.page,
                    xPct: fractionToCssPercent(option.position.xPct),
                    yPct: fractionToCssPercent(option.position.yPct),
                    widthPct: fractionToCssPercent(option.position.widthPct),
                    heightPct: fractionToCssPercent(option.position.heightPct),
                });
            }),
        };
    }));
}
//# sourceMappingURL=choice_questions.js.map